/**
 * Dashboard overview API — first page of the admin dashboard.
 * Isolated from marketing-site APIs in `src/api/`.
 * When USE_DASHBOARD_REAL_BACKEND is false, mock data is returned (same pattern as day trips).
 */

import { bookingAssets, shellAssets, statAssets } from '../../constants/assets'
import { servicesSlices } from '../../data/bookingServices'
import { highlightStat as defaultHighlightStat, stats as defaultStats } from '../../data/stats'
import { formatRevenueK } from '../../utils/chartMath'
import type {
    RecentBooking,
    StatCard,
  UpcomingTrip,
} from '../../types'
import {
  DASHBOARD_API_BASE_URL,
  USE_DASHBOARD_REAL_BACKEND,
  type DashboardApiResponse,
} from '../config/config'
import {
  ADMIN_AUTH_STORAGE_KEY,
  ADMIN_TOKEN_KEY,
  ADMIN_USER_KEY,
} from '../../auth/adminAuth'
import type {
  DashboardOverview,
  RevenueRangeKey,
  RevenueSeriesPayload,
} from './overviewTypes'

export type {
  DashboardOverview,
  DashboardOverviewRevenue,
  DashboardOverviewServices,
  DashboardOverviewUser,
  RevenueRangeKey,
  RevenueSeriesPayload,
} from './overviewTypes'

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {}

const getNestedValue = (source: Record<string, unknown>, path: string) => {
  const segments = path.split('.')
  let current: unknown = source

  for (const segment of segments) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return undefined
    }

    const next = (current as Record<string, unknown>)[segment]
    if (next === undefined || next === null || next === '') {
      return undefined
    }

    current = next
  }

  return current
}

const pickFirst = (source: Record<string, unknown>, candidates: string[]) => {
  for (const key of candidates) {
    const directValue = source[key]
    if (directValue !== undefined && directValue !== null && directValue !== '') {
      return directValue
    }

    const nestedValue = getNestedValue(source, key)
    if (nestedValue !== undefined && nestedValue !== null && nestedValue !== '') {
      return nestedValue
    }
  }

  return undefined
}

const normalizeString = (value: unknown) =>
  String(value ?? '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')

const normalizeBookingStatus = (value: unknown) => {
  const normalized = normalizeString(value)

  if (normalized.includes('in progress')) return 'in-progress'
  if (normalized.includes('completed')) return 'confirmed'
  if (normalized.includes('confirmed')) return 'confirmed'
  if (normalized.includes('pending')) return 'pending'
  if (normalized.includes('cancel')) return 'cancelled'

  return 'confirmed'
}

const normalizeStatCard = (
  raw: unknown,
  fallback: StatCard,
): StatCard => {
  const data = asRecord(raw)
  return {
    id: String(data.id ?? fallback.id),
    label: String(data.label ?? fallback.label),
    value: String(data.value ?? fallback.value),
    icon: (data.icon as StatCard['icon']) ?? fallback.icon,
    chipSize: Number(data.chipSize ?? fallback.chipSize),
    glyphSize: Number(data.glyphSize ?? fallback.glyphSize),
    flipped: Boolean(data.flipped ?? fallback.flipped),
    glyphInset:
      typeof data.glyphInset === 'string' ? data.glyphInset : fallback.glyphInset,
    caption: String(data.caption ?? fallback.caption),
    showTrend: Boolean(data.showTrend ?? fallback.showTrend),
  }
}

const monthOrder = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
]

const monthSortIndex = (month: string) => {
  const normalized = normalizeString(month).slice(0, 3)
  const index = monthOrder.indexOf(normalized)
  return index >= 0 ? index : monthOrder.length
}

const niceRevenueMax = (value: number) => {
  if (!Number.isFinite(value) || value <= 0) return 1000
  const magnitude = 10 ** Math.floor(Math.log10(value))
  const normalized = value / magnitude
  const nice =
    normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10
  return nice * magnitude
}

const buildRevenueAxisLabels = (maxValue: number) =>
  [maxValue, maxValue * 0.75, maxValue * 0.5, maxValue * 0.25, 0].map((value) =>
    formatRevenueK(Number.isFinite(value) ? value : 0),
  )

const serviceNameToId = (value: unknown) => {
  const normalized = normalizeString(value)

  if (
    normalized.includes('private van tour') ||
    normalized.includes('private van tours') ||
    normalized.includes('private tour') ||
    normalized.includes('private tours')
  ) {
    return 'private'
  }

  if (normalized.includes('airport transfer')) {
    return 'airport'
  }

  if (normalized.includes('ski transfer')) {
    return 'ski'
  }

  if (
    normalized.includes('day trip') ||
    normalized.includes('day trips') ||
    normalized.includes('daytrip') ||
    normalized.includes('day trip hallstatt') ||
    normalized.includes('wattens') ||
    normalized.includes('eagles nest') ||
    normalized.includes('hallstatt')
  ) {
    return 'day'
  }

  if (
    normalized.includes('city tour') ||
    normalized.includes('city tours') ||
    normalized.includes('shuttle service') ||
    normalized.includes('shuttle services') ||
    normalized.includes('events & transfers') ||
    normalized.includes('events and transfers') ||
    normalized.includes('event transfers') ||
    normalized.includes('corporate transfer') ||
    normalized.includes('corporate transfers') ||
    normalized.includes('vip transfer') ||
    normalized.includes('vip transfers') ||
    normalized.includes('sightseeing') ||
    normalized.includes('sightseeing tours') ||
    normalized.includes('other')
  ) {
    return 'others'
  }

  return undefined
}

const serviceCategoryToId = (value: unknown) => {
  const normalized = normalizeString(value)
  if (!normalized) return undefined

  if (normalized.includes('private')) return 'private'
  if (normalized.includes('airport')) return 'airport'
  if (normalized.includes('ski')) return 'ski'
  if (normalized.includes('day') || normalized.includes('daytrip')) return 'day'
  if (
    normalized.includes('city') ||
    normalized.includes('shuttle') ||
    normalized.includes('event') ||
    normalized.includes('corporate') ||
    normalized.includes('vip') ||
    normalized.includes('sightseeing') ||
    normalized.includes('other')
  ) {
    return 'others'
  }

  return undefined
}

const formatMetricValue = (value: unknown): string => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value.toLocaleString('en-US')
  }

  if (typeof value === 'string' && value.trim()) {
    return value.trim()
  }

  return ''
}

const formatCurrencyValue = (value: unknown): string => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value.toLocaleString('en-US')
  }

  if (typeof value === 'string' && value.trim()) {
    return value.trim()
  }

  return ''
}

const formatPercentValue = (value: number): string => {
  if (!Number.isFinite(value)) return ''

  const absoluteValue = Math.abs(value)
  if (Number.isInteger(absoluteValue)) {
    return absoluteValue.toLocaleString('en-US')
  }

  return absoluteValue.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })
}

const formatChangePeriod = (value: unknown): string => {
  const normalized = String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')

  if (!normalized) return 'last period'

  const periodMap: Record<string, string> = {
    previous_month: 'last month',
    last_month: 'last month',
    month: 'last month',
    yesterday: 'yesterday',
    previous_week: 'last week',
    last_week: 'last week',
    week: 'last week',
    previous_year: 'last year',
    last_year: 'last year',
    year: 'last year',
  }

  return periodMap[normalized] ?? normalized.replace(/_/g, ' ')
}

const resolveSummaryTrend = (
  raw: Record<string, unknown>,
  fallback: StatCard,
  changeKeys: string[],
  periodKeys: string[],
) => {
  const changeValue = Number(pickFirst(raw, changeKeys))
  const periodValue = pickFirst(raw, periodKeys)

  if (!Number.isFinite(changeValue) && !periodValue) {
    return {
      caption: fallback.caption,
      showTrend: Boolean(fallback.showTrend),
    }
  }

  if (!Number.isFinite(changeValue)) {
    return {
      caption: `from ${formatChangePeriod(periodValue)}`,
      showTrend: false,
    }
  }

  const signed = changeValue > 0 ? '+' : changeValue < 0 ? '-' : ''
  return {
    caption: `${signed}${formatPercentValue(changeValue)}% from ${formatChangePeriod(periodValue ?? 'last period')}`,
    showTrend: changeValue !== 0,
  }
}

const normalizeWeeklyLabel = (point: {
  weekLabel: string
  weekNumber: number
  start: string
  end: string
  month: string
  period: string
}) => {
  const rawWeek = point.weekLabel || (Number.isFinite(point.weekNumber) ? String(point.weekNumber) : '')
  const match = rawWeek.match(/(\d+)/)
  const weekNumber = Number(match?.[1] ?? point.weekNumber)

  if (Number.isFinite(weekNumber)) return `W${weekNumber}`
  if (point.start && point.end) return `W${point.weekNumber}`
  return point.month || point.period || ''
}

const normalizeRevenueSeries = (
  raw: unknown,
  fallback: RevenueSeriesPayload,
  range: RevenueRangeKey,
): RevenueSeriesPayload => {
  const rawArray = Array.isArray(raw) ? raw : null
  const data = asRecord(raw)

  if (rawArray) {
    const points = rawArray
      .map((item) => asRecord(item))
      .map((item) => {
        const rawWeek = pickFirst(item, ['week', 'week_number', 'weekNumber'])
        const weekLabel = String(rawWeek ?? '').trim()
        const weekNumber = Number(rawWeek)
        const start = String(pickFirst(item, ['start_date', 'start', 'from', 'begin']) ?? '').trim()
        const end = String(pickFirst(item, ['end_date', 'end', 'to']) ?? '').trim()
        const yearRaw = pickFirst(item, ['year'])
        const year = Number(yearRaw)

        return {
          month: String(pickFirst(item, ['month', 'label', 'name']) ?? '').trim(),
          period: String(pickFirst(item, ['period', 'month', 'label']) ?? '').trim(),
          year: Number.isFinite(year) ? year : NaN,
          weekLabel,
          weekNumber: Number.isFinite(weekNumber) ? weekNumber : NaN,
          start,
          end,
          total: Number(pickFirst(item, ['total', 'amount', 'value', 'revenue']) ?? 0),
        }
      })
      .filter(
        (point) =>
          point.month ||
          point.period ||
          point.weekLabel ||
          point.start ||
          point.end ||
          Number.isFinite(point.year),
      )
      .sort((left, right) => {
        if (left.start && right.start) {
          return new Date(left.start).getTime() - new Date(right.start).getTime()
        }

        const leftHasYear = Number.isFinite(left.year)
        const rightHasYear = Number.isFinite(right.year)

        if (leftHasYear || rightHasYear) {
          const ly = leftHasYear ? left.year : 0
          const ry = rightHasYear ? right.year : 0
          if (ly !== ry) return ly - ry
          const lw = Number.isFinite(left.weekNumber) ? left.weekNumber : 0
          const rw = Number.isFinite(right.weekNumber) ? right.weekNumber : 0
          return lw - rw
        }

        return monthSortIndex(left.month) - monthSortIndex(right.month)
      })

    const valuesK = points.map((point) =>
      Number.isFinite(point.total) ? Number(point.total) : 0,
    )

    const xLabels = points.map((point) => {
      if (range === 'Weekly') {
        return normalizeWeeklyLabel(point)
      }

      if (range === 'Yearly') {
        if (Number.isFinite(point.year)) return String(point.year)
        return point.month || point.period || ''
      }

      return point.month || point.period || ''
    })

    const periods = points.map((point) => {
      if (range === 'Weekly') {
        if (point.start && point.end) return `${point.start} → ${point.end}`
        if (!Number.isNaN(point.weekNumber)) return `W${point.weekNumber}`
        return point.period || point.month || ''
      }

      if (range === 'Yearly') {
        if (Number.isFinite(point.year)) return String(point.year)
        return point.period || point.month || ''
      }

      return point.period || point.month || ''
    })
    const inferredMax = niceRevenueMax(Math.max(...valuesK, 0.1))
    const axisLabels = buildRevenueAxisLabels(inferredMax)

    return {
      xLabels: xLabels.length ? xLabels : fallback.xLabels,
      periods: periods.length ? periods : fallback.periods,
      valuesK: valuesK.length ? valuesK : fallback.valuesK,
      maxK: Number.isFinite(inferredMax) ? inferredMax : fallback.maxK,
      axisLabels: axisLabels.length ? axisLabels : fallback.axisLabels,
    }
  }

  const valuesK = Array.isArray(data.valuesK)
    ? data.valuesK.map((value) => Number(value)).filter((value) => !Number.isNaN(value))
    : Array.isArray(data.values)
      ? data.values.map((value) => Number(value)).filter((value) => !Number.isNaN(value))
      : fallback.valuesK

  const xLabels = Array.isArray(data.xLabels)
    ? data.xLabels.map(String)
    : Array.isArray(data.labels)
      ? data.labels.map(String)
      : fallback.xLabels

  const periods = Array.isArray(data.periods)
    ? data.periods.map(String)
    : xLabels

  const axisLabels = Array.isArray(data.axisLabels)
    ? data.axisLabels.map(String)
    : Array.isArray(data.axis_labels)
      ? data.axis_labels.map(String)
      : fallback.axisLabels

  const inferredMax = Number(
    pickFirst(data, ['maxK', 'max_k', 'maxValue', 'max_value', 'max']) ?? fallback.maxK,
  )

  return {
    xLabels: xLabels.length ? xLabels : fallback.xLabels,
    periods: periods.length ? periods : fallback.periods,
    valuesK: valuesK.length ? valuesK : fallback.valuesK,
    maxK: Number.isFinite(inferredMax) ? inferredMax : fallback.maxK,
    axisLabels: axisLabels.length ? axisLabels : fallback.axisLabels,
  }
}

const normalizeTrip = (raw: unknown, index: number): UpcomingTrip => {
  const data = asRecord(raw)
  return {
    id: String(
      pickFirst(data, ['id', 'bookingId', 'booking_id', 'booking.id']) ?? `trip-${index}`,
    ),
    time: String(
      pickFirst(data, ['pickup_time', 'pickupTime', 'time', 'startTime', 'scheduledAt']) ?? '',
    ),
    bookingId: String(
      pickFirst(data, ['booking_id', 'bookingId', 'reference_number', 'reference']) ?? '',
    ),
    service: String(
      pickFirst(data, ['service.name', 'service', 'service_name', 'serviceName']) ?? '',
    ),
    date: String(
      pickFirst(data, ['date', 'pickup_date', 'pickupDate', 'tripDate', 'scheduledDate']) ?? '',
    ),
    customer: String(
      pickFirst(data, ['customer.name', 'customer', 'customer_name', 'customerName']) ?? '',
    ),
    status: normalizeBookingStatus(pickFirst(data, ['status', 'booking_status', 'trip_status'])),
  }
}

const normalizeRecentBooking = (raw: unknown, index: number): RecentBooking => {
  const data = asRecord(raw)
  return {
    id: String(
      pickFirst(data, ['id', 'bookingId', 'booking_id', 'booking.id']) ?? `booking-${index}`,
    ),
    title: String(
      pickFirst(data, ['title', 'service.name', 'service', 'service_name', 'serviceName']) ?? '',
    ),
    date: String(
      pickFirst(data, ['booking_date', 'bookingDate', 'date', 'createdAt', 'scheduledAt']) ?? '',
    ),
    guests: String(
      pickFirst(data, ['passenger_count', 'passengers', 'adult_count', 'guests', 'guestCount']) ?? '',
    ),
    status: normalizeBookingStatus(pickFirst(data, ['status', 'booking_status'])),
    reference: String(
      pickFirst(data, ['reference_number', 'reference', 'booking_id', 'bookingId', 'id']) ?? '',
    ),
    amount: formatCurrencyValue(
      pickFirst(data, ['amount', 'total_amount', 'totalAmount', 'final_price', 'finalPrice', 'price']) ??
        0,
    ),
    thumbnail: String(
      pickFirst(data, ['thumbnail', 'image', 'image_url', 'service.image', 'service.thumbnail']) ??
        bookingAssets.thumb,
    ),
  }
}

const EMPTY_DASHBOARD_OVERVIEW: DashboardOverview = {
  user: {
    name: 'Admin',
    email: 'admin@salzburg.at',
    avatar: shellAssets.avatar,
  },
  welcomeSubtitle: 'Dashboard Overview',
  highlightStat: defaultHighlightStat,
  stats: defaultStats,
  revenue: {
    defaultActiveIndex: 5,
    series: {
      Monthly: {
        xLabels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
        periods: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
        valuesK: [0, 0, 0, 0, 0, 0],
        maxK: 100,
        axisLabels: ['0K', '25K', '50K', '75K', '100K'],
      },
      Weekly: {
        xLabels: ['W1', 'W2', 'W3', 'W4', 'W5', 'W6'],
        periods: ['W1', 'W2', 'W3', 'W4', 'W5', 'W6'],
        valuesK: [0, 0, 0, 0, 0, 0],
        maxK: 50,
        axisLabels: ['0K', '10K', '20K', '30K', '40K', '50K'],
      },
      Yearly: {
        xLabels: ['2021', '2022', '2023', '2024', '2025', '2026'],
        periods: ['2021', '2022', '2023', '2024', '2025', '2026'],
        valuesK: [0, 0, 0, 0, 0, 0],
        maxK: 1000,
        axisLabels: ['0K', '250K', '500K', '750K', '1000K'],
      },
    },
  },
  bookingServices: {
    totalCount: 0,
    slices: [],
  },
  upcomingTrips: [],
  recentBookings: [],
}

/** Normalize backend/mock payloads into the shape the overview UI expects. */
export function normalizeDashboardOverview(
  raw: Partial<DashboardOverview> | Record<string, unknown>,
): DashboardOverview {
  const data = asRecord(raw)
  const fallback = EMPTY_DASHBOARD_OVERVIEW
  const userRaw = asRecord(data.user)
  const summaryRaw = asRecord(
    data.summary_cards ?? data.summaryCards ?? data.summary,
  )
  const monthlyRevenueRaw =
    data.monthly_revenue ?? data.monthlyRevenue ?? data.revenue

  const series = {
    Monthly: normalizeRevenueSeries(monthlyRevenueRaw, fallback.revenue.series.Monthly, 'Monthly'),
    Weekly: normalizeRevenueSeries(
      data.weekly_revenue ?? data.weeklyRevenue,
      fallback.revenue.series.Weekly,
      'Weekly',
    ),
    Yearly: normalizeRevenueSeries(
      data.yearly_revenue ?? data.yearlyRevenue,
      fallback.revenue.series.Yearly,
      'Yearly',
    ),
  } satisfies Record<RevenueRangeKey, RevenueSeriesPayload>

  const serviceDistributionArray = Array.isArray(data.service_distribution)
    ? data.service_distribution
    : Array.isArray(data.serviceDistribution)
      ? data.serviceDistribution
      : Array.isArray(data.bookingServices)
        ? data.bookingServices
        : Array.isArray(data.booking_services)
          ? data.booking_services
          : []

  const rawTotalCount = serviceDistributionArray
    .map((item) => asRecord(item))
    .reduce((sum, item) => {
      const count = Number(
        pickFirst(item, ['count', 'value', 'total', 'bookings', 'total_count', 'booking_count']) ??
          0,
      )
      return sum + (Number.isFinite(count) ? count : 0)
    }, 0)

  const backedServiceCounts = serviceDistributionArray
    .map((item) => asRecord(item))
    .reduce<Record<string, number>>((map, item) => {
      const serviceName = pickFirst(item, [
        'name',
        'label',
        'service_name',
        'serviceName',
        'service.name',
        'service__name',
      ])
      const categoryName = pickFirst(item, [
        'category',
        'category_name',
        'categoryName',
        'service.category',
        'service.category.name',
        'service__category__name',
        'service_category',
      ])

      const id =
        serviceNameToId(serviceName) ||
        serviceCategoryToId(categoryName) ||
        serviceNameToId(categoryName) ||
        serviceCategoryToId(serviceName)

      if (!id) return map
      const count = Number(
        pickFirst(item, ['count', 'value', 'total', 'bookings', 'total_count', 'booking_count']) ??
          0,
      )
      if (!Number.isFinite(count)) return map

      map[id] = (map[id] ?? 0) + count
      return map
    }, {})

  const totalCount = rawTotalCount > 0 ? rawTotalCount : Object.values(backedServiceCounts).reduce(
    (sum, count) => sum + count,
    0,
  )

  const normalizedSlices = servicesSlices.map((slice) => {
    const count = backedServiceCounts[slice.id] ?? 0
    const percent = totalCount > 0 ? Math.round((count / totalCount) * 100) : 0
    return {
      ...slice,
      count,
      percent,
      share: `${percent}%(${count})`,
    }
  })

  const summaryStatCards: Array<{
    id: string
    label: string
    value: string
    fallback: StatCard
    changeKeys: string[]
    periodKeys: string[]
    totalVehicles?: number
  }> = [
    {
      id: 'total-bookings',
      label: 'Total Bookings',
      value: formatMetricValue(
        pickFirst(summaryRaw, ['total_bookings', 'totalBookings', 'bookings']) ??
          data.total_bookings ??
          data.totalBookings,
      ),
      fallback: fallback.highlightStat,
      changeKeys: ['total_bookings_change', 'totalBookingsChange', 'bookings_change'],
      periodKeys: ['total_bookings_change_period', 'totalBookingsChangePeriod', 'bookings_change_period'],
    },
    {
      id: 'todays-trips',
      label: "Today's Trips",
      value: formatMetricValue(
        pickFirst(summaryRaw, ['today_trips', 'todayTrips', 'todays_trips']) ??
          data.today_trips ??
          data.todays_trips ??
          data.todaysTrips,
      ),
      fallback: fallback.stats[0],
      changeKeys: ['today_trips_change', 'todayTripsChange', 'todays_trips_change'],
      periodKeys: ['today_trips_change_period', 'todayTripsChangePeriod', 'todays_trips_change_period'],
    },
    {
      id: 'upcoming-trips',
      label: 'Upcoming Trips',
      value: formatMetricValue(
        pickFirst(summaryRaw, ['upcoming_trips', 'upcomingTrips']) ??
          data.upcoming_trips ??
          data.upcomingTrips,
      ),
      fallback: fallback.stats[1],
      changeKeys: ['upcoming_trips_change', 'upcomingTripsChange'],
      periodKeys: ['upcoming_trips_change_period', 'upcomingTripsChangePeriod'],
    },
    {
      id: 'total-revenue',
      label: 'Total Revenue',
      value: formatMetricValue(
        pickFirst(summaryRaw, ['total_revenue', 'totalRevenue', 'revenue']) ??
          data.total_revenue ??
          data.totalRevenue,
      ),
      fallback: fallback.stats[2],
      changeKeys: ['total_revenue_change', 'totalRevenueChange', 'revenue_change'],
      periodKeys: ['total_revenue_change_period', 'totalRevenueChangePeriod', 'revenue_change_period'],
    },
    {
      id: 'active-vehicles',
      label: 'Active Vehicles',
      value: formatMetricValue(
        pickFirst(summaryRaw, ['active_vehicles', 'activeVehicles', 'vehicles']) ??
          data.active_vehicles ??
          data.activeVehicles,
      ),
      fallback: fallback.stats[3],
      changeKeys: [],
      periodKeys: [],
      totalVehicles: Number(
        pickFirst(summaryRaw, ['total_vehicles', 'totalVehicles']) ??
          data.total_vehicles ??
          data.totalVehicles ??
          0,
      ),
    },
  ]

  const statsSource = summaryStatCards.slice(1).map((stat) => {
    const trend = resolveSummaryTrend(summaryRaw, stat.fallback, stat.changeKeys, stat.periodKeys)
    return {
      ...stat,
      caption: trend.caption,
      showTrend: trend.showTrend,
    }
  })

  const highlightCard = (() => {
    const trend = resolveSummaryTrend(
      summaryRaw,
      fallback.highlightStat,
      summaryStatCards[0].changeKeys,
      summaryStatCards[0].periodKeys,
    )

    return {
      ...summaryStatCards[0],
      caption: trend.caption,
      showTrend: trend.showTrend,
    }
  })()

  const activeVehiclesCard = (() => {
    const rawTotal = Number(
      pickFirst(summaryRaw, ['total_vehicles', 'totalVehicles']) ??
        data.total_vehicles ??
        data.totalVehicles ??
        0,
    )

    const caption = Number.isFinite(rawTotal) && rawTotal >= 0
      ? `Out of ${rawTotal} vehicles`
      : summaryStatCards[4].fallback.caption

    return {
      ...summaryStatCards[4],
      caption,
      showTrend: false,
    }
  })()

  return {
    user: {
      name: String(userRaw.name ?? fallback.user.name),
      email: String(userRaw.email ?? fallback.user.email),
      avatar: String(userRaw.avatar ?? shellAssets.avatar),
    },
    welcomeSubtitle: String(
      data.welcomeSubtitle ?? data.welcome_subtitle ?? fallback.welcomeSubtitle,
    ),
    highlightStat: normalizeStatCard(highlightCard, {
      ...fallback.highlightStat,
      icon: fallback.highlightStat.icon || statAssets.bookings,
      value: highlightCard.value ?? fallback.highlightStat.value,
      caption: highlightCard.caption ?? fallback.highlightStat.caption,
      showTrend: highlightCard.showTrend ?? fallback.highlightStat.showTrend,
    }),
    stats: statsSource.map((stat, index) =>
      normalizeStatCard(
        index === 3 ? activeVehiclesCard : stat,
        {
          ...stat.fallback,
          value: stat.value ?? stat.fallback.value,
          caption: stat.caption ?? stat.fallback.caption,
          showTrend: stat.showTrend ?? stat.fallback.showTrend,
        },
      ),
    ),
    revenue: {
      defaultActiveIndex: Number(
        data.defaultActiveIndex ??
          data.default_active_index ??
          fallback.revenue.defaultActiveIndex,
      ),
      series,
    },
    bookingServices: {
      totalCount,
      slices: normalizedSlices,
    },
    upcomingTrips: (
      Array.isArray(data.upcoming_trips_table)
        ? data.upcoming_trips_table
        : Array.isArray(data.upcomingTrips)
          ? data.upcomingTrips
          : Array.isArray(data.upcoming_trips)
            ? data.upcoming_trips
            : fallback.upcomingTrips
    ).map((trip, index) => normalizeTrip(trip, index)),
    recentBookings: (
      Array.isArray(data.recent_bookings)
        ? data.recent_bookings
        : Array.isArray(data.recentBookings)
          ? data.recentBookings
          : fallback.recentBookings
    ).map((booking, index) => normalizeRecentBooking(booking, index)),
  }
}

/**
 * GET /api/admin/dashboard/overview
 * Returns every widget on the first dashboard page in one response.
 */
export async function apiGetDashboardOverview(): Promise<
  DashboardApiResponse<DashboardOverview>
> {
  if (!USE_DASHBOARD_REAL_BACKEND) {
    return {
      success: true,
      message: 'Dashboard overview loaded successfully.',
      data: normalizeDashboardOverview({}),
    }
  }

  try {
    const token = localStorage.getItem('salzburg_admin_token')
    const baseUrl = DASHBOARD_API_BASE_URL.replace(/\/$/, '')
    const urls = [
      `${baseUrl}/api/admin/dashboard/`,
      `${baseUrl}/api/admin/dashboard/overview`,
    ]

    let response: Response | null = null
    let result: Record<string, unknown> = {}
    let lastError = ''

    for (const url of urls) {
      try {
        const requestHeaders: HeadersInit = {}

        if (token && token !== 'demo_admin_token_active') {
          requestHeaders.Authorization = `Bearer ${token}`
        }

        const candidate = await fetch(url, {
          method: 'GET',
          headers: requestHeaders,
        })
        const payload = await candidate.json().catch(() => null)

        if (candidate.ok) {
          response = candidate
          result = asRecord(payload)
          break
        }

        lastError = `${candidate.status} ${candidate.statusText}`.trim()
      } catch (error) {
        lastError = error instanceof Error ? error.message : 'Request failed'
      }
    }

    if (!response || !response.ok) {
      const unauthorized = lastError.startsWith('401')

      if (unauthorized) {
        localStorage.removeItem(ADMIN_AUTH_STORAGE_KEY)
        localStorage.removeItem(ADMIN_TOKEN_KEY)
        localStorage.removeItem(ADMIN_USER_KEY)
      }

      return {
        success: false,
        message: typeof result.message === 'string'
          ? result.message
          : 'Failed to fetch dashboard overview.',
        warning: `Tried ${urls.join(' and ')}${lastError ? ` (${lastError})` : ''}`,
        unauthorized,
      }
    }

    const payload = result?.data ?? result

    return {
      success: true,
      message: typeof result.message === 'string'
        ? result.message
        : 'Dashboard overview loaded successfully.',
      data: normalizeDashboardOverview(asRecord(payload)),
    }
  } catch {
    return {
      success: false,
      message: 'Connection failed.',
      warning: `Unable to reach dashboard API at ${DASHBOARD_API_BASE_URL}.`,
    }
  }
}
